import { Button, FormControl } from '@mui/material';
import { toastError, toastSuccess } from '@onestep/coreui/Toast';
import classNames from 'classnames';
import { find, includes, isEmpty, isNumber, map } from 'lodash';
import { useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import { PROCEDURE_OPTIONS } from '@/consts/selectOptions';

import CustomDatePicker from '../../../../../components/CustomDatePicker';
import Select from '../../../../../components/Inputs/Select';
import TextField from '../../../../../components/Inputs/TextField';
import useMixpanel from '../../../../../hooks/useMixpanel';
import useClinicalInfoService from '../../../../../services/patientProfileService/clinicalInfo';
import type { ISurgeryOption } from '../../../../../services/patientProfileService/clinicalInfo/types';
import { getDate } from '../../../../../services/timeService/index';
import { dateTimeFormat } from '../../../../../services/timeService/types';
import { eventModalContext } from '../../../Timeline';
import styles from './index.module.scss';

interface ISurgeryOptionForm extends ISurgeryOption {
  other?: string;
}

export default function SurgeryForm() {
  const { patientUuid = '' } = useParams<{ patientUuid: string }>();
  const { trackClick } = useMixpanel();
  const { data, surgery: surgeryAction } = useClinicalInfoService(patientUuid);
  const { eventUuid, setEventUuid } = useContext(eventModalContext);
  const surgeryId = eventUuid;
  const surgery = find(data?.surgeries, ['id', surgeryId]) ?? ({} as ISurgeryOption);

  const newItem = surgeryId === 'none';
  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    ...(!isEmpty(surgery) && {
      defaultValues: {
        ...surgery,
        ...(!includes(map(PROCEDURE_OPTIONS, 'value'), surgery.name) && {
          name: 'other',
          other: surgery.name,
        }),
      },
    }),
  });
  const isOtherOption = watch('name') === 'other';

  const onSubmit = async (formData: ISurgeryOptionForm) => {
    if (isOtherOption) {
      formData.name = formData.other ?? '';
      delete formData.other;
    }

    try {
      if (newItem) {
        await surgeryAction.addSurgery({ ...formData, patientUuid });
        trackClick({
          action: 'add_event',
        });
      } else if (isNumber(surgeryId)) {
        await surgeryAction.editSurgery({
          data: { ...formData, patientUuid },
          surgeryId: surgeryId,
        });
      }
      toastSuccess({ content: `Surgery ${newItem ? 'created' : 'edited'} successfully!` });
    } catch {
      toastError({ content: `Failed to ${newItem ? 'create' : 'edit'} surgery!` });
    } finally {
      setEventUuid();
    }
  };

  return (
    <form className="flex flex-col px-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col">
        <FormControl
          error={!!errors?.eventDate}
          required
          className={classNames({
            [styles.error]: errors?.eventDate,
          })}
        >
          <Controller
            render={({ field }) => (
              <CustomDatePicker
                label={'Event Time'}
                {...register('eventDate', {
                  required: 'This field is required',
                })}
                isRequired
                onChange={(date) => {
                  field.onChange(date?.format(dateTimeFormat.SERVER_DATE_FORMAT));
                }}
                defaultValue={surgery?.eventDate ? getDate(surgery.eventDate) : null}
                isError={!!errors?.eventDate}
                helperText={errors?.eventDate?.message as string}
                useBlackBorder
                labelClassName={styles.datePicker}
              />
            )}
            name="eventDate"
            control={control}
          />
        </FormControl>
      </div>
      <div className="flex flex-col">
        <span className={styles.fieldLabel}>
          Procedure
          <span className={styles.required}>*</span>
        </span>
        <Controller
          render={({ field }) => (
            <Select
              variant="standard"
              options={PROCEDURE_OPTIONS}
              className={classNames(styles.input, 'mb-2')}
              disableUnderline
              {...field}
            />
          )}
          name="name"
          control={control}
          defaultValue={''}
          rules={{
            required: {
              value: true,
              message: 'This field is required',
            },
          }}
        />
        {isOtherOption ? (
          <TextField
            {...register('other', {
              required: {
                value: true,
                message: 'This field is required',
              },
            })}
            fullWidth
            wrapperClassName="mb-2"
            placeholder="Surgery name"
          />
        ) : null}
      </div>
      {!isEmpty(errors) ? (
        <div className={classNames('color-error', styles.error)}>Please enter required fields</div>
      ) : null}
      <div className={classNames('flex justify-center', styles.btnContainer)}>
        <Button type="submit" color="primary" variant="contained" classes={{ root: styles.addBtn }}>
          {isSubmitting ? 'Loading ...' : newItem ? 'Add' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
